import React, { useState, useEffect, useRef } from "react";
import {
  FormControl,
  Grid,
  TextField,
  Snackbar,
  Alert as MuiAlert,
  Button,
  Typography,
  Box,
  Container,
  InputAdornment,
  IconButton,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import Layout from "../../../layout/Layout";
import axios from "axios";
import { emailConfigurationAPI, config } from "../../../config";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";

function EmailConfiguration() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isMounted = useRef(false);

  const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  });

  const validationSchema = Yup.object().shape({
    sender_email: Yup.string()
      .required("Sender Email is required")
      .email("Please enter valid email format"),
    host: Yup.string().required("SMTP Host is required"),
    port: Yup.number()
      .typeError("Port must be a number")
      .required("Port is required"),
    auth_user: Yup.string().required("Auth User is required"),
    auth_pass: Yup.string().required("Auth Password is required"),
  });

  const { register, handleSubmit, reset, formState } = useForm({
    resolver: yupResolver(validationSchema),
  });
  const { errors } = formState;

  useEffect(() => {
    if (!isMounted.current) {
      fetchConfig();
      isMounted.current = true;
    }
  }, []);

  const fetchConfig = () => {
    config.method = "get";
    config.url = emailConfigurationAPI;
    axios(config)
      .then((response) => {
        if (response.data.data && response.data.data.length > 0) {
          reset(response.data.data[0]);
        }
      })
      .catch((error) => {
        console.error("Error fetching email configuration:", error);
      });
  };

  const onSubmit = (data) => {
    setIsSubmitting(true);
    const userData = JSON.parse(localStorage.getItem("user"));
    const postData = {
      ...data,
      intUserID: userData ? userData.id : 0,
    };

    config.method = "post";
    config.url = emailConfigurationAPI;
    config.data = JSON.stringify(postData);

    axios(config)
      .then((response) => {
        setOpen(true);
        setMessage(response.data.message || "Settings updated successfully");
        setMessageType("success");
        setIsSubmitting(false);
        setTimeout(() => {
          navigate("/agent/dashboard");
        }, 2000);
      })
      .catch((error) => {
        console.error("Error saving email configuration:", error);
        setOpen(true);
        setMessage("Failed to update settings");
        setMessageType("error");
        setIsSubmitting(false);
      });
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  const handleClickShowPassword = () => setShowPassword(!showPassword);

  return (
    <Layout>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 9999,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
        open={isSubmitting}
      >
        <CircularProgress color="inherit" size={56} />
        <Typography variant="h6" color="inherit">
          Please wait...
        </Typography>
      </Backdrop>

      <Snackbar open={open} autoHideDuration={5000} onClose={handleClose}>
        <Alert
          onClose={handleClose}
          severity={messageType}
          sx={{ width: "100%" }}
        >
          {message}
        </Alert>
      </Snackbar>
      <form onSubmit={handleSubmit(onSubmit)} className="white-bg">
        <Container>
          <Typography variant="h5" sx={{ mb: 3 }}>
            Email Server Configuration
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={6} md={6}>
              <FormControl fullWidth>
                <TextField
                  label="Sender Email"
                  {...register("sender_email")}
                  error={!!errors.sender_email}
                  helperText={errors.sender_email?.message}
                  focused
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} lg={6} md={6}>
              <FormControl fullWidth>
                <TextField
                  label="SMTP Host"
                  {...register("host")}
                  error={!!errors.host}
                  helperText={errors.host?.message}
                  focused
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} lg={6} md={6}>
              <FormControl fullWidth>
                <TextField
                  label="SMTP Port"
                  {...register("port")}
                  error={!!errors.port}
                  helperText={errors.port?.message}
                  focused
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} lg={6} md={6}>
              <FormControl fullWidth>
                <TextField
                  label="SMTP User"
                  {...register("auth_user")}
                  error={!!errors.auth_user}
                  helperText={errors.auth_user?.message}
                  focused
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} lg={6} md={6}>
              <FormControl fullWidth>
                <TextField
                  label="SMTP Password"
                  type={showPassword ? "text" : "password"}
                  {...register("auth_pass")}
                  error={!!errors.auth_pass}
                  helperText={errors.auth_pass?.message}
                  focused
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  size="large"
                  disabled={isSubmitting}
                >
                  Save Configuration
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </form>
    </Layout>
  );
}

export default EmailConfiguration;
